import classNames from "classnames";
import React, { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory, useParams } from "react-router";
import { EventsState } from "../../../store/types";
import { TemplateClassNames, TemplateNames } from "../../../types/template-layouts";
import { BlProfile, BrandliveEvent, LanguagesAbbr, PageModuleGroupModules, PageModuleType, RegistrationStepType, Session, SessionTabsLayoutTypes, TicketingSet, TranslateString } from "../../../types/working-model";
import Icon from "../../general-ui/icon";
import { ParamsProps } from '../live-event';
import SessionStream from "./session-stream/session-stream";
import './session.scss';
import SessionDocuments from "./session-modules/documents/documents";
import Sticky from "../../general-ui/sticky/sticky";
import SessionSpeakers from "./session-modules/speakers/speakers";
import SessionDescription from "./session-modules/description/description";
import SessionSimilarSessions from "./session-modules/similar-sessions/similar-sessions";
import SessionProducts from "./session-modules/products/products";
// import SessionQuizzes from "./session-modules/quizzes/quizzes";
import SessionSurvey from "./session-modules/survey/survey";
import SessionQuestions from "./session-modules/questions/questions";
import { getUserSession, setUserSession } from "../../../store/actions/event/event-actions";
import { getStorageItem } from "../../../utils/local-storage";
import SessionHeader from "./session-modules/header/header";
import { replaceSpaceWithDash } from "../../../utils/utils";
import { eventPath } from "../utils";
import { useScreenMediaQuery } from "../../../utils/use-screen-media-query";
import LiveChat from "./live-chat/live-chat";
import { loadChatMessages } from "../../../store/actions/event/chat-actions";
import SessionDocumentHead from "./session-document-head";
import MarketingNav from "../marketing-page/navigation/marketing-nav";
import useTranslationModules from "../../../i18n/useTranslationModules";
import { useTranslation } from "react-i18next";
import BlankSection from "../modules/blank-section/blank-section";

export enum ETabLayoutClassNames {
	noTabs = 'no-tabs',
	vertical = 'vertical',
	horizontal = 'horizontal'
}

interface RenderSessionModulesProps {
	session: Session;
	template: TemplateClassNames;
	moduleGroup: PageModuleGroupModules;
	preview?: boolean;
}
const RenderSessionModules: React.FC<RenderSessionModulesProps> = ({ session, template, moduleGroup, preview }) => {
	const modules = useMemo(() => {
		const tabLayoutClassname = replaceSpaceWithDash(session.layout?.tabs?.layout.toLowerCase() || ETabLayoutClassNames.horizontal);

		return moduleGroup?.modules.map(moduleId => {
			const module = session?.modules.find(module => module.id === moduleId);
			if (!module || !module.is_on || !moduleGroup.is_on || (module.modules && module.modules.length === 0)) return null;
			switch (module.type) {
				case (PageModuleType.description): {
					return <SessionDescription key={module.id} module={module} template={template} />;
				}
				case (PageModuleType.speakers): {
					return <SessionSpeakers key={module.id} module={module} template={template} tabLayout={tabLayoutClassname as ETabLayoutClassNames} />;
				}
				case (PageModuleType.similar_sessions): {
					return <SessionSimilarSessions key={module.id} module={module} template={template} />;
				}
				case (PageModuleType.products): {
					return <SessionProducts key={module.id} module={module} template={template} tabLayout={tabLayoutClassname as ETabLayoutClassNames} />;
				}
				case (PageModuleType.quizzes): {
					// FUTURE DEV - hiding quizzes and posts
					// return <SessionQuizzes key={module.id} module={module} template={template} />;
					return;
				}
				case (PageModuleType.survey): {
					return <SessionSurvey key={module.id} module={module} template={template} session={session.session} sessionUuid={session.uuid} preview={preview} />;
				}
				case (PageModuleType.documents): {
					return <SessionDocuments key={module.id} module={module} template={template} />;
				}
				case (PageModuleType.questions): {
					return <SessionQuestions key={module.id} module={module} template={template} session={session.session} session_uuid={session.uuid} />;
				}
				case (PageModuleType.embed_widget):
				case (PageModuleType.blank): {
					return <BlankSection key={module.id} module={module} template={template} />;
				}
			}
		});

	}, [session, template, moduleGroup, preview]);

	return <>{modules}</>;
};

interface SessionViewProps {
	singleSession?: boolean;
	previewBundle?: BrandliveEvent;
	previewSession?: Session;
	previewLanguage?: LanguagesAbbr;
}

const SessionView: React.FC<SessionViewProps> = ({ singleSession, previewBundle, previewSession, previewLanguage }) => {
	const {
		LiveEventReducer: {
			loadingEventBundle,
			eventBundle,
			validationChecked,
			validRegistration,
			gettingUserSession,
			blProfileUserToken,
			playbackUrls,
			getUserSessionError,
			blProfileUser,
			registrationId
		},
		CreateEventReducer: {
			editorSize,
			workingEvent
		}
	} = useSelector((event: EventsState) => event);

	const dispatch = useDispatch();
	const history = useHistory();
	const [selectedTabUuid, setSelectedTabUuid] = useState<string>();
	const { sessionUuid, uuid, eventName, language }: ParamsProps = useParams();
	const [chatClosed, setChatClosed] = useState(true);
	// const [simulateFullscreen, setSimulateFullscreen] = useState(false);
	const {
		isLessThan640
	} = useScreenMediaQuery();

	const isPreview = !!(previewBundle || previewSession);

	const previewLessThan1024 = (editorSize === 'tablet') || (editorSize === 'mobile');
	const session = useMemo(() => {
		if (singleSession) {
			return previewBundle?.sessions?.[0] ? previewBundle?.sessions?.[0] : eventBundle!.sessions?.[0];
		}
		if (previewSession) {
			return previewSession;
		}
		return eventBundle?.sessions?.find(session => session.uuid === sessionUuid);
	}, [singleSession, eventBundle, previewBundle, sessionUuid, previewSession]);

	const logo = isPreview ? workingEvent?.homepage?.header.logo : eventBundle?.homepage?.header.logo;

	const notValidTranslation = !session?.languages.includes(language);

	useTranslationModules({ language: notValidTranslation ? session?.default_language : language });

	useEffect(() => {
		//check to make sure language is in translated list, if not, push them to the default language
		if (notValidTranslation && session && !isPreview) {
			if (singleSession) {
				history.replace(`/${eventPath(eventName, uuid)}/${session.default_language}`);
			} else {
				history.replace(`/${eventPath(eventName, uuid)}/${session.default_language}/session/${sessionUuid}`);
			}
		}
	}, [history, eventName, uuid, sessionUuid, session, notValidTranslation, isPreview, singleSession]);

	const { t } = useTranslation("session");

	useEffect(() => {
		// set default tab to the first group
		if (session) setSelectedTabUuid(session.module_grouping?.[0].uuid);
	}, [session]);

	const channel = eventBundle?.channel;
	const ticketingStep = eventBundle?.registration_steps?.find(step => step.type === RegistrationStepType.ticketing && step.isOn);

	let ticket: string;
	let ticketData: TicketingSet | undefined;
	let hasTicket = false;

	if (channel && session) {
		ticket = blProfileUser?.profile?.[channel].ticket;
		ticketData = ticketingStep?.ticketing?.find(_ticket => _ticket.uuid == ticket);
		hasTicket = ticketData?.session_uuids.indexOf(session.uuid) !== -1;
	}

	//loading chat messages in this component rather than in the chat component
	//because that component moves around in the DOM due to the designs
	useEffect(() => {
		if (session && !isPreview)
			dispatch(loadChatMessages(session.uuid, language));
	}, [session, language, dispatch, isPreview]);

	const layout = session?.layout?.tabs?.layout || previewSession?.layout?.tabs?.layout || 'Horizontal';

	// Removes spaces for use in className: 'no-tabs','horizontal','vertical'
	const layoutClassName = layout?.toLowerCase().replace(" ", "-");

	const showIcons = session?.layout?.tabs?.show_icons ?? true;

	//DEV NOTE: to change a template and get the fonts and color palettes to 
	//load properly you must go back to the edotir and publish a new event with the desired theme settings
	const template = eventBundle?.template.name || previewBundle?.template.name || TemplateNames.Classic;

	const handleTabChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
		setSelectedTabUuid(target.value);
	};

	useEffect(() => {
		// getUserSessionError prevents this from turning into an infinite loop.
		// if the dispatch(getUSerSession...) lambda call fails, then getUserSessionError will be the result string message
		if (!gettingUserSession && session && !getUserSessionError && !isPreview) {
			const storedSession = getStorageItem(`ses.${session.session}`);
			if (storedSession) {
				dispatch(setUserSession(storedSession));
			} else {
				dispatch(getUserSession(session.session, blProfileUserToken, registrationId ?? undefined));
			}
		}
	}, [gettingUserSession, session, dispatch, blProfileUserToken, getUserSessionError, registrationId, isPreview]);

	const tabGroupMap = useMemo(() => {
		return session?.module_grouping?.reduce((acc, curr) => {
			acc[curr.uuid] = curr;
			return acc;
		}, {} as { [key: string]: PageModuleGroupModules });
	}, [session]);

	const groupedNavButtons = useMemo(() => {
		return session?.module_grouping?.map(({ name, uuid, icon, is_on, modules }) => {
			if (!is_on || !modules.length) return null;

			//If there is no content in the module don't make the tab
			const _modules = session.modules.filter(module => modules.includes(module.id!));

			function notSupposedToHaveModules(type: PageModuleType) {
				switch (type) {
					case PageModuleType.description:
					case PageModuleType.questions:
					case PageModuleType.blank:
					case PageModuleType.embed_widget: return true;
					default: return false;
				}
			}
			const hasContent = _modules.some(_module => notSupposedToHaveModules(_module.type) || !!_module.modules);

			if (!hasContent) return null;

			let _name: string;
			//Check to see if "_name" is a Translate string object, if so return the translated value, if not, then it is already a string. Use static translation if available or return the string
			const translateName = name as TranslateString;
			if (typeof translateName === "object") {
				_name = translateName[language] as string ?? translateName.base;
			} else {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				_name = t(name) ?? name;
			}
			return (
				<li key={uuid}>
					<input
						type="radio"
						name="tab-navigation"
						id={`session-${uuid}`}
						value={uuid}
						onChange={handleTabChange}
						checked={uuid === selectedTabUuid}
					/>
					<label htmlFor={`session-${uuid}`}>
						<Icon name={icon || ""} size={16} color={classNames({ noIcon: !showIcons })} />
						<span>{_name}</span>
					</label>
				</li>
			);
		});
	}, [session?.module_grouping, selectedTabUuid, showIcons, t, session?.modules, language]);

	const renderModulesByGroup = () => {
		if (!selectedTabUuid || !session || !tabGroupMap) return <></>;
		const templateClassName = TemplateClassNames[template];
		return <RenderSessionModules moduleGroup={tabGroupMap[selectedTabUuid]} session={session} template={templateClassName} preview={previewSession ? true : false} />;
	};

	const renderAllModules = () => {
		if (!session || !tabGroupMap) return <></>;
		const templateClassName = TemplateClassNames[template];
		return (
			<>
				{session?.module_grouping?.map(group => <RenderSessionModules key={group.uuid} moduleGroup={group} session={session} template={templateClassName} preview={previewSession ? true : false} />)}
			</>
		);
	};

	// the tab-navigation component is the session navigation panel,
	// not to be confused with a tab-header component.  
	const renderContent = () => {
		switch (layout) {
			case SessionTabsLayoutTypes.Vertical:
				return (
					<>
						<ul className="tab-navigation">
							{groupedNavButtons}
						</ul>
						<div className="page-container-body">
							{renderModulesByGroup()}
						</div>
					</>
				);
			case SessionTabsLayoutTypes.NoTabs:
				return (
					<div className="page-container-body">
						{renderAllModules()}
					</div>
				);
			default: //Horizontal
				return (
					<>
						<Sticky
							top={session && session.languages?.length > 1 ? "70px" : '0px'}
							renderStickyElements={() => (
								<ul className="tab-navigation">
									{groupedNavButtons}
								</ul>
							)}
						/>
						<div className="page-container-body">
							{renderModulesByGroup()}
						</div>
					</>
				);
		}
	};

	const handleChatClose = useCallback((chatClosed: boolean) => {
		setChatClosed(chatClosed);
	}, []);

	// useEffect(() => {
	// 	if (isPreview) return;

	// 	const handleOrientationChange = () => {
	// 		if (window.innerHeight > window.innerWidth) {
	// 			setSimulateFullscreen(true);
	// 		} else {
	// 			setSimulateFullscreen(false);
	// 		}
	// 	};

	// 	console.log("Added orientation change event...");
	// 	window.addEventListener('orientationchange', handleOrientationChange, true);

	// 	return () => {
	// 		window.removeEventListener('orientationchange', handleOrientationChange);
	// 	};
	// }, [isPreview]);

	//no redirect if this is the preview
	if (eventBundle?.registration_on && !registrationId && !isPreview) return <Redirect to={`/${eventPath(eventName, uuid)}/${language}/registration`} />;

	if (loadingEventBundle || !session) {
		return (
			<div className={classNames('session', TemplateClassNames[template])}>
				<div className={"session-main-content"}>
					<div className={classNames("modules-container", layoutClassName)}>
					</div>
				</div>
			</div>
		);
	}

	//do not redirect if this is the preview
	//user has invalid validation OR has not yet registered and registration is on, so redirect to registration page
	if (eventBundle?.registration_on && validationChecked && !validRegistration && !isPreview) {
		return <Redirect to={`/${eventPath(eventName, uuid)}/${language}/registration`} />;
	}

	if (eventBundle?.registration_on && ticketingStep && !hasTicket && !isPreview) {
		return <Redirect to={`/${eventPath(eventName, uuid)}/${language}`} />;
	}

	const hasNavigation = eventBundle?.homepage;

	const previewNavigation = previewBundle?.homepage;

	const isDesktop = editorSize === 'desktop';

	const styleOverrides = () => {
		const backgroundColor = session?.video?.customizations?.backgroundColor;
		// Apollo desktop has a gradient
		if (backgroundColor && TemplateClassNames[template] === TemplateClassNames.Apollo && isDesktop) {
			return { background: `linear-gradient(180deg, rgba(0, 0, 0, .3) 0%, rgba(var(--${backgroundColor}-rgb), 0)`, backgroundColor: `var(--${backgroundColor})` };
		}

		if (backgroundColor) {
			return { background: `var(--${backgroundColor})` };
		}

		return {};
	};

	if (previewSession && previewBundle) {
		return (
			<div className={classNames('session', TemplateClassNames[template])}>
				{
					previewNavigation && <MarketingNav template={TemplateClassNames[template]} previewHide={previewLessThan1024} logo={logo ?? undefined} previewLanguage={previewLanguage} />
				}
				<div className={classNames("session-main-content", { "single-session": previewBundle ? !previewNavigation : !hasNavigation })}>
					<SessionDocumentHead session={session} />
					<SessionHeader session={session} eventBundle={previewBundle} styleOverrides={styleOverrides()} previewLanguage={previewLanguage} />
					<SessionStream language={language} session={session} eventBundle={previewBundle} playback={playbackUrls[session.uuid]} editorSize={editorSize} />
					{editorSize === 'mobile' && (
						<LiveChat
							session={session}
							loading={!!loadingEventBundle}
							language={language}
							registrationOn={!!previewBundle?.registration_on}
							blProfileUser={blProfileUser as BlProfile}
							template={TemplateClassNames[previewBundle.template.name]}
							forceOverlay={false}
							mobileOnly={true}
							isEditor={true}
							eventBundle={previewBundle}
							chatClosed={chatClosed}
							onChatClose={handleChatClose}
						/>
					)}
					<div className={classNames("modules-container", layoutClassName)}>
						{renderContent()}
					</div>
				</div>
			</div>
		);
	}

	return eventBundle ? (
		<div className={classNames('session', TemplateClassNames[template])}>
			{
				// single session events can not have a homepage
				hasNavigation && <MarketingNav template={TemplateClassNames[template]} logo={logo ?? undefined} />
			}
			<div className={classNames("session-main-content", { 'single-session': !eventBundle.homepage })}>
				<SessionDocumentHead session={session} />
				<SessionHeader session={session} eventBundle={eventBundle} styleOverrides={styleOverrides()} previewLanguage={previewLanguage} />
				<SessionStream language={language} session={session} eventBundle={eventBundle} playback={playbackUrls[`${session.uuid}-${language}`]} />
				{isLessThan640 && (
					<LiveChat
						session={session}
						loading={!!loadingEventBundle}
						language={language}
						registrationOn={!!eventBundle?.registration_on}
						blProfileUser={blProfileUser as BlProfile}
						template={TemplateClassNames[eventBundle.template.name]}
						forceOverlay={false}
						mobileOnly={true}
						chatClosed={chatClosed}
						onChatClose={handleChatClose}
						eventBundle={eventBundle}
					/>
				)}
				<div className={classNames("modules-container", layoutClassName)}>
					{renderContent()}
				</div>
			</div>
		</div>
	) : previewBundle ? (
		<div className={classNames('session', TemplateClassNames[template])}>
			<SessionHeader styleOverrides={styleOverrides()} session={session} eventBundle={previewBundle} previewLanguage={previewLanguage} />
			<SessionStream language={language} session={session} eventBundle={previewBundle} playback={playbackUrls[`${session.uuid}-${language}`]} />
			<div className={classNames("modules-container", layoutClassName)}>
				{renderContent()}
			</div>
		</div>
	) : null;
};

export default SessionView;

